import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { AcceptCallActionData, CallInvitation } from '../../types/call';
import { CallStoreService } from '../../services/call-store.service';
import { NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { NzAvatarModule } from 'ng-zorro-antd/avatar';
import { CommonModule } from '@angular/common';
import { getColorByName, getListContactFromLocal } from '../../../../utils/utils';
import { ToastrService } from 'ngx-toastr';
import { firstValueFrom } from 'rxjs';
import { MeetingService } from '../../../meeting-ui/service/meeting.service';
import { BroadcastService } from '../../../../services/broadcast.service';
import {WindowService} from '../../../../services/window.service';
import { CallAudioService } from '../../services/call-audio.service';
import { RouteStateService } from '../../services/route-state.service';
import { LocalMediaPermissionService } from '../../../../services/local-media-permission.service';
import { ModalPermissionComponent } from '../../../meeting-management/components/modal-permission/modal-permission.component';
@Component({
  selector: 'app-incoming-call-modal',
  standalone: true,
  imports: [NzAvatarModule, CommonModule],
  templateUrl: './incoming-call-modal.component.html',
  styleUrl: './incoming-call-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class IncomingCallModalComponent {
  // inviter lấy từ zego => lấy theo hệ thống
  data?: { call: CallInvitation, inviter: any }
  private modalRef = inject(NzModalRef);
  private callStore = inject(CallStoreService);
  private toastr = inject(ToastrService);
  private meetingService = inject(MeetingService);
  private broadcastService = inject(BroadcastService);
  private windowService = inject(WindowService);
  private callAudio = inject(CallAudioService);
  private routeState = inject(RouteStateService);
  private localMediaPermissionService = inject(LocalMediaPermissionService);
  private nzModal = inject(NzModalService);
  
  readonly isInMeetingRoute = this.routeState.isInRoute("/meeting");

  readonly isMicPermissionGranted = this.localMediaPermissionService.isMicPermissionGranted;
  readonly isCameraPermissionGranted = this.localMediaPermissionService.isCameraPermissionGranted;

  private contacts: any[] = [];
  getColorByName = getColorByName;
  displayName = signal<string>('Unknown');
  avatarUrl = '';
  avatarBg = '';
  getListContactFromLocal = getListContactFromLocal;

  get isWaiting() {
    return this.data && this.data.call && this.data.call.status === 'waiting';
  }

  constructor() {
    this.broadcastService.call$.subscribe(async ({ action, payload }) => {
      switch (action) {
        case 'reject-switch-call':
          this.reject();
          break;
        }
    })
  }

  async ngOnInit(): Promise<void> {
    if (this.modalRef) {
      this.data = this.modalRef.getConfig().nzData;
      await this.loadContacts();
      this.resolveInviter();
    }
  }

  private async loadContacts() {
    this.contacts = await getListContactFromLocal();
  }

  private normalizePhone(phone?: string): string {
    if (!phone) return '';
    return phone
      .replace('+84', '0')
      .replace(/^84/, '0')
      .trim();
  }

  private resolveInviter(): void {
    const inviterId = this.normalizePhone(this.data?.call?.inviter);
    if (!inviterId) {
      return;
    }
    // lấy từ danh bạ để compare
    const fromStorage = this.contacts.find(
      c => c.zegoId === inviterId
    );

    const i = fromStorage ?? {
      phoneNumber: inviterId,
      contactName: null,
      username: inviterId
    };

    // const conversationNameCache = JSON.parse(localStorage.getItem(`conversation_name_cache`) || '{}');

    this.displayName.set(
      i.contactName ??
      this.data?.call.extendedData?.callerName ??
      i.phoneNumber ??
      'Unknown');

    // this.displayName.set(i.contactName ? i.contactName : inviterPhone);

    this.avatarUrl =
      i.imageUrl ?? this.data?.inviter?.baseInfo?.userAvatarUrl ?? '';

    this.avatarBg = getColorByName(this.displayName());
  }

  async accept() {
    const checkPermission = await this.localMediaPermissionService.handleCheckDevicePermissionCall(this.data!.call!.type);
    if(!checkPermission) return;

    let isInMeeting = !!localStorage.getItem("ACTIVE_MEETING");
    // let ongoing = await this.checkOngoingStatus();
    // if (ongoing) {
    //   this.toastr.info("Bạn đang trong cuộc họp/cuộc gọi khác. Vui lòng thử lại.");
    //   return;
    // }
    this.modalRef.close();

    this.modalRef.afterClose.subscribe(() => {
      this.callAudio.stop();
      this.broadcastService.call({ action: 'clear-ringtone', payload: {} });
      if (this.data?.call.status === 'waiting' && !isInMeeting) {
        if (this.callStore.isInCallRoute()) {
          this.callStore.acceptCall(this.data?.call.callId);
          return;
        } else {
          this.broadcastService.call({ action: 'switch-call', payload: { callId: this.data?.call.callId } });
          return;
        }
      }
      // TODO: clean this up

      const newTab = this.windowService.createCallWindow();

      const data: AcceptCallActionData = {
        currentCall: this.callStore.currentCall(),
        callState: this.callStore.callState(),
        participants: this.callStore.participants(),
        pendingInvitation: this.callStore.pendingInvitation()
      };

      const messageData = { action: 'accept-call', payload: data };

      // this.callStore.setPendingInvitation(null);

      setTimeout(() => {
        newTab?.postMessage(JSON.stringify(messageData), window.location.origin);
      }, 2000);
    })
  }


  async checkOngoingStatus() {
    try {
      let status = await firstValueFrom(this.meetingService.getOngoingRooms());

      if (!status.success) {
        throw new Error(status.message);
      }

      return status.data.isOngoing;
    } catch (e) {
      console.log('error determining if meeting ongoing', e)
      return true;
    }
  }

  reject() {
    if (this.data) {
      const isRejectSwitchCall = this.data.call.status === 'waiting';
      if (this.callStore.isInCallRoute()) {
        this.callStore.rejectCall(this.data.call.callId, isRejectSwitchCall);
        this.modalRef.close();
      } else {
        if(isRejectSwitchCall){
          // Khi đang trong meeting
          if(this.isInMeetingRoute()){
            this.callStore.rejectCall(this.data.call.callId, isRejectSwitchCall);
          } else {
            this.broadcastService.call({ action: 'reject-switch-call', payload: {} });
          }
        } else {
          this.callStore.rejectCall(this.data.call.callId, isRejectSwitchCall);
          this.modalRef.close();
        }
      }   
    }
  }
}
